import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Globe, TrendingUp, ChevronRight, Sparkles, Filter, Download, Radio, FileText, Bookmark, ArrowUpRight, Shield, AlertTriangle, Database, Activity, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import InteractiveMap from '../components/InteractiveMap';
import TrendChart from '../components/TrendChart';
import { evaluateRisk } from '../utils/riskEvaluation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CountryDashboard({ user: propUser }) {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [feedItems, setFeedItems] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [imfData, setImfData] = useState(null);
  const [sourcesStats, setSourcesStats] = useState(null);
  const [user, setUser] = useState(propUser || null);

  useEffect(() => {
    if (propUser) { setUser(propUser); return; }
    // Fallback: fetch from /api/auth/me
    axios.get(`${API}/auth/me`).then(r => setUser(r.data)).catch(() => {});
  }, [propUser]);

  useEffect(() => {
    axios.get(`${API}/countries`)
      .then(res => setCountries(res.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
    
    // Fetch intelligence feed (may take a moment on first load)
    axios.get(`${API}/intelligence-feed`,{timeout: 15000 })
      .then(res => setFeedItems(res.data.feed || []))
      .catch(() => {})
      .finally(() => setFeedLoading(false));
    
    // Fetch IMF global indicators
    axios.get(`${API}/analytics/global-indicators`, {timeout: 25000})
      .then(res => setImfData(res.data))
      .catch(() => {});
    
    // Fetch sources stats
    axios.get(`${API}/sources/stats`)
      .then(res => setSourcesStats(res.data))
      .catch(() => {});
  }, []);

  const totalSections = countries.reduce((acc, c) => acc + (c.sections_generated?.length || 0), 0);
  const activeReports = countries.filter(c => (c.sections_generated?.length || 0) > 0).length;
  const riskCounts = countries.reduce((acc, c) => {
    const r = evaluateRisk(c).level;
    acc[r] = (acc[r] || 0) + 1;
    return acc;
  }, {});
  const recentCountries = [...countries].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)).slice(0, 3);
  
  // Dynamic greeting — prefer actual first name, fall back to email prefix
  const hour = new Date().getHours();
  const timeGreeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const rawFirst = (user?.full_name || '').trim().split(' ')[0] || '';
  const emailHandle = user?.email?.split('@')[0] || '';
  // If full_name is missing or looks like a role label, prefer the email handle
  const roleLike = !rawFirst || ['admin', 'administrator', 'user', 'analyst', 'member'].includes(rawFirst.toLowerCase()) || rawFirst.toLowerCase() === (user?.role || '').toLowerCase();
  const firstName = roleLike
    ? (emailHandle ? emailHandle.charAt(0).toUpperCase() + emailHandle.slice(1) : 'Analyst')
    : rawFirst;
  const inProgress = countries.filter(c => {
    const n = c.sections_generated?.length || 0;
    return n > 0 && n < 30;
  }).length;

  if (loading) return <div className="flex items-center justify-center h-screen text-[#434655]"><Sparkles className="w-6 h-6 animate-pulse mr-2" /> Loading intelligence...</div>;

  return (
    <div data-testid="country-dashboard" className="min-h-screen bg-[#f8f9ff] text-[#0b1c30]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeInUp { animation: fadeInUp 0.5s ease-out forwards; }
        @keyframes slowSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-slowSpin { animation: slowSpin 30s linear infinite; }
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .animate-marquee { animation: marquee 60s linear infinite; }
        .dash-grid-bg { background-image: linear-gradient(rgba(0,74,198,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,74,198,0.05) 1px, transparent 1px); background-size: 40px 40px; }
      `}</style>
      
      {/* Dynamic Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f8f9ff] via-white to-[#eff4ff] border-b border-[#c3c6d7]/20">
        <div className="absolute inset-0 dash-grid-bg opacity-50" />
        <div className="absolute top-1/2 right-0 translate-x-1/3 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#004ac6]/5 via-[#7c3aed]/5 to-transparent animate-slowSpin pointer-events-none" />
        <div className="relative px-6 lg:px-8 py-8">
          <div className="inline-flex items-center gap-2 bg-white border border-[#c3c6d7]/40 rounded-full px-3 py-1 mb-4 shadow-sm animate-fadeInUp">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#434655]">Platform · Welcome back</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tighter text-[#0b1c30] leading-[1.1] animate-fadeInUp" style={{ animationDelay: '0.1s' }} data-testid="dashboard-greeting">
            {timeGreeting}, <span className="bg-gradient-to-r from-[#004ac6] to-[#7c3aed] bg-clip-text text-transparent">{firstName}</span>
            <span className="text-[#434655] font-semibold text-xl md:text-2xl">
              {' · '}
              {countries.length === 0
                ? 'ready to scaffold your first country blueprint?'
                : inProgress > 0
                  ? `${inProgress} blueprint${inProgress > 1 ? 's' : ''} in progress · ${activeReports - inProgress} complete`
                  : activeReports > 0
                    ? `${activeReports} blueprint${activeReports > 1 ? 's' : ''} ready for review`
                    : `${countries.length} countr${countries.length > 1 ? 'ies' : 'y'} on your radar`}
            </span>
          </h1>
          <p className="text-sm text-[#434655] mt-2 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            Real-time geopolitical monitoring and sovereign risk assessment for tier-one strategic assets.
          </p>
        </div>
        {/* Slim live ticker */}
        {feedItems.length > 0 && (
          <div className="bg-[#0b1c30] py-2 overflow-hidden border-t border-white/10">
            <div className="flex gap-6 animate-marquee whitespace-nowrap">
              {[...feedItems.slice(0, 20), ...feedItems.slice(0, 20)].map((it, i) => {
                const positive = it.raw_value >= 0;
                return (
                  <div key={i} className="flex items-center gap-1.5 text-white/80 text-[11px]">
                    <span className="text-[#a78bfa] font-bold">{it.country}</span>
                    <span className="text-white/40">·</span>
                    <span className="text-white/60">{it.indicator}:</span>
                    <span className={`font-mono font-bold ${positive ? 'text-emerald-400' : 'text-red-400'}`}>{it.value}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>
      
      <div className="p-6 lg:p-8">
      {/* Page Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tighter text-[#0b1c30] mb-1">Country Intelligence</h1>
          <p className="text-sm text-[#434655]">Real-time geopolitical monitoring and sovereign risk assessment for tier-one strategic assets.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" className="gap-2 text-xs text-[#434655] border-[#c3c6d7]/40">
            <Filter className="w-3.5 h-3.5" /> Filter Matrix
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 text-xs text-[#434655] border-[#c3c6d7]/40" data-testid="export-feed-btn">
                <Download className="w-3.5 h-3.5" /> Export Data
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem
                data-testid="export-feed-csv"
                onClick={() => {
                  const url = `${API}/intelligence-feed/export?format=csv`;
                  window.open(url, '_blank');
                  toast.success('Downloading CSV…');
                }}>
                <FileText className="w-3.5 h-3.5 mr-2" /> Download as CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="export-feed-xlsx"
                onClick={() => {
                  const url = `${API}/intelligence-feed/export?format=xlsx`;
                  window.open(url, '_blank');
                  toast.success('Downloading Excel…');
                }}>
                <FileSpreadsheet className="w-3.5 h-3.5 mr-2" /> Download as Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Map + Status + Targets */}
        <div className="lg:col-span-2 space-y-6">
          {/* Sovereign Risk Matrix - Interactive Map */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-5 pb-3">
              <h2 className="text-sm font-bold text-[#0b1c30] tracking-tight mb-1">Sovereign Risk Matrix</h2>
              <p className="text-xs text-[#434655]">Live Geographic Exposure — Click any marker to view blueprint</p>
            </div>
            <div className="px-5 pb-4">
              <InteractiveMap
                countries={countries}
                onCountryClick={(country) => navigate(`/platform/countries/${country.id}/blueprint`)}
              />
            </div>
          </div>

          {/* System Status Bar */}
          <div className="bg-[#0b1c30] text-white p-5 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Radio className="w-4 h-4 text-green-400" />
              <span className="text-sm font-bold">Intelligence Stream Active</span>
            </div>
            <p className="text-xs text-white/60 mb-4">Monitoring {countries.length} territories. Risk evaluation based on sovereign credit ratings, HDI scores, and governance metrics.</p>
            <div className="flex items-center gap-8">
              <div>
                <span className="text-[10px] text-white/40 uppercase tracking-widest">Stable</span>
                <p className="text-xl font-bold text-[#3b82f6]">{String(riskCounts['STABLE'] || 0).padStart(2, '0')}</p>
              </div>
              <div>
                <span className="text-[10px] text-white/40 uppercase tracking-widest">Caution</span>
                <p className="text-xl font-bold text-[#f59e0b]">{String((riskCounts['CAUTION'] || 0) + (riskCounts['MODERATE'] || 0)).padStart(2, '0')}</p>
              </div>
              <div>
                <span className="text-[10px] text-white/40 uppercase tracking-widest">High Risk</span>
                <p className="text-xl font-bold text-[#ef4444]">{String(riskCounts['HIGH RISK'] || 0).padStart(2, '0')}</p>
              </div>
              <div>
                <span className="text-[10px] text-white/40 uppercase tracking-widest">Unassessed</span>
                <p className="text-xl font-bold text-white/50">{String(riskCounts['UNASSESSED'] || 0).padStart(2, '0')}</p>
              </div>
            </div>
          </div>

          {/* Historical Trend Chart */}
          <TrendChart />

          {/* IMF Global Forecasts Widget */}
          {imfData && imfData.countries?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-5 pb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-bold text-[#0b1c30] tracking-tight">IMF Economic Forecasts</h2>
                  <p className="text-[10px] text-[#434655]">World Economic Outlook — GDP Growth & Inflation projections</p>
                </div>
                <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full uppercase tracking-wider">IMF WEO</span>
              </div>
              <div className="px-5 pb-4 overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[#c3c6d7]/20">
                      <th className="text-[10px] font-bold uppercase tracking-widest text-[#434655] py-2 pr-4">Country</th>
                      <th className="text-[10px] font-bold uppercase tracking-widest text-[#434655] py-2 px-3 text-center" colSpan={2}>GDP Growth (%)</th>
                      <th className="text-[10px] font-bold uppercase tracking-widest text-[#434655] py-2 px-3 text-center" colSpan={2}>Inflation (%)</th>
                    </tr>
                    <tr className="border-b border-[#c3c6d7]/10">
                      <th></th>
                      <th className="text-[9px] text-[#434655] py-1 px-3 text-center">2025</th>
                      <th className="text-[9px] text-[#434655] py-1 px-3 text-center">2026</th>
                      <th className="text-[9px] text-[#434655] py-1 px-3 text-center">2025</th>
                      <th className="text-[9px] text-[#434655] py-1 px-3 text-center">2026</th>
                    </tr>
                  </thead>
                  <tbody>
                    {imfData.countries.slice(0, 12).map((c, i) => {
                      const gdp = c.indicators?.['GDP Growth (%)'] || {};
                      const inf = c.indicators?.['Inflation (%)'] || {};
                      return (
                        <tr key={i} className="border-b border-[#c3c6d7]/10 hover:bg-[#f8f9ff] transition-colors">
                          <td className="py-2 pr-4 text-xs font-semibold text-[#0b1c30]">{c.country}</td>
                          <td className={`py-2 px-3 text-xs font-bold text-center ${(gdp['2025'] || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{gdp['2025'] ?? '—'}</td>
                          <td className={`py-2 px-3 text-xs font-bold text-center ${(gdp['2026'] || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{gdp['2026'] ?? '—'}</td>
                          <td className={`py-2 px-3 text-xs font-bold text-center ${(inf['2025'] || 0) > 10 ? 'text-red-500' : 'text-[#0b1c30]'}`}>{inf['2025'] ?? '—'}</td>
                          <td className={`py-2 px-3 text-xs font-bold text-center ${(inf['2026'] || 0) > 10 ? 'text-red-500' : 'text-[#0b1c30]'}`}>{inf['2026'] ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-5 pb-3 pt-1 border-t border-[#c3c6d7]/20 text-center">
                <span className="text-[10px] text-[#434655]">Source: {imfData.source} | {imfData.countries.length} countries tracked</span>
              </div>
            </div>
          )}

          {/* Data Sources Summary */}
          {sourcesStats && (
            <div className="bg-[#0b1c30] text-white p-5 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Database className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-bold">Intelligence Data Network</span>
                <span className="ml-auto text-[9px] font-bold text-emerald-400 bg-emerald-900/30 px-2 py-0.5 rounded-full">{sourcesStats.total} SOURCES</span>
              </div>
              <div className="grid grid-cols-4 gap-3 mb-3">
                <div>
                  <span className="text-[10px] text-white/40 uppercase tracking-widest">Total</span>
                  <p className="text-xl font-bold text-white">{sourcesStats.total}</p>
                </div>
                <div>
                  <span className="text-[10px] text-white/40 uppercase tracking-widest">APIs</span>
                  <p className="text-xl font-bold text-emerald-400">{sourcesStats.api_total}</p>
                </div>
                <div>
                  <span className="text-[10px] text-white/40 uppercase tracking-widest">Categories</span>
                  <p className="text-xl font-bold text-white">{sourcesStats.categories?.length}</p>
                </div>
                <div>
                  <span className="text-[10px] text-white/40 uppercase tracking-widest">Critical</span>
                  <p className="text-xl font-bold text-red-400">{sourcesStats.priorities?.find(p => p.name === 'Critical')?.count || 0}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {sourcesStats.categories?.slice(0, 5).map((cat, i) => (
                  <span key={i} className="text-[9px] bg-white/10 text-white/70 px-2 py-0.5 rounded-full">
                    {cat.name.split(':')[0].split('&')[0].trim()} ({cat.count})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Key Intelligence Targets */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-[#0b1c30] tracking-tight">Key Intelligence Targets</h2>
              <button onClick={() => navigate('/platform/countries')} className="text-xs text-[#004ac6] font-semibold flex items-center gap-1 hover:underline">
                View All Territories <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {recentCountries.map((country) => {
                const progress = country.sections_generated?.length || 0;
                const risk = evaluateRisk(country);
                const gdpGrowth = country.metrics_gdp_growth;
                const creditRating = country.metrics_credit_rating;
                const sovereignScore = country.metrics_sovereign_score;
                return (
                  <div key={country.id} onClick={() => navigate(`/platform/countries/${country.id}/blueprint`)}
                    className="bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-all group"
                    data-testid={`target-${country.id}`}>
                    <div className="h-28 bg-gradient-to-br from-[#1a365d] to-[#004ac6] relative overflow-hidden">
                      <div className="absolute inset-0 opacity-30" style={{ backgroundImage: `radial-gradient(circle at 50% 50%, #2563eb 0%, transparent 70%)` }} />
                      <div className="absolute bottom-3 left-4 right-4">
                        <h3 className="text-base font-bold text-white tracking-tight">{country.name}</h3>
                        <p className="text-[10px] text-white/60 uppercase tracking-widest">{country.region || 'Unclassified'}</p>
                      </div>
                      <span className={`absolute top-3 right-3 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        risk.color === 'green' ? 'bg-green-500/20 text-green-300' :
                        risk.color === 'red' ? 'bg-red-500/20 text-red-300' :
                        risk.color === 'yellow' ? 'bg-yellow-500/20 text-yellow-300' :
                        'bg-white/10 text-white/60'
                      }`}>{risk.level}</span>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="flex justify-between text-xs">
                        <div>
                          <span className="text-[#434655]">GDP Growth</span>
                          <p className="font-bold text-[#0b1c30]">{gdpGrowth != null ? `+${gdpGrowth}%` : '—'}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-[#434655]">{creditRating ? 'Rating' : 'Sovereign Score'}</span>
                          <p className="font-bold text-[#0b1c30]">{creditRating ? creditRating.split(';')[0].trim() : (sovereignScore != null ? `${sovereignScore}/10` : '—')}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-[#434655]">{progress}/31 sections</span>
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                          progress >= 30 ? 'bg-green-50 text-green-600' : progress >= 12 ? 'bg-emerald-50 text-emerald-600' : progress > 0 ? 'bg-[#e5eeff] text-[#004ac6]' : 'bg-[#eff4ff] text-[#434655]'
                        }`}>{progress >= 30 ? 'Sovereign' : progress >= 22 ? 'Strategist' : progress >= 12 ? 'Scout Complete' : progress > 0 ? 'In Progress' : 'New'}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Column: Feed + Blueprint + Watchlist */}
        <div className="space-y-6">
          {/* Intelligence Feed — Real World Bank Data */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-[#0b1c30] tracking-tight">Intelligence Feed</h2>
              <span className="text-[9px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full uppercase tracking-wider">World Bank</span>
            </div>
            {feedLoading ? (
              <div className="py-6 text-center">
                <div className="inline-flex items-center gap-2 text-xs text-[#434655]">
                  <div className="w-3 h-3 rounded-full border-2 border-[#004ac6] border-t-transparent animate-spin" />
                  Fetching economic indicators...
                </div>
              </div>
            ) : feedItems.length > 0 ? (
              <>
                <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1 scrollbar-thin">
                {feedItems.map((item, i) => {
                  const borderColor = 
                    item.indicator === 'GDP Growth' ? '#3b82f6' : 
                    item.indicator === 'Inflation' ? '#ef4444' : 
                    item.indicator === 'Unemployment' ? '#f59e0b' : 
                    item.indicator === 'Current Account' ? '#8b5cf6' :
                    item.indicator === 'Trade/GDP' ? '#059669' :
                    item.indicator === 'Govt Debt/GDP' ? '#dc2626' :
                    '#004ac6';
                  return (
                    <div key={i} className="border-l-2 pl-3 py-1" style={{ borderColor }}>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-[#434655] font-medium">{item.country}</span>
                        <span className="text-[9px] font-bold text-[#004ac6] bg-[#eff4ff] px-1.5 py-0.5 rounded">{item.value}</span>
                      </div>
                      <h4 className="text-xs font-semibold text-[#0b1c30] mt-0.5 leading-snug">{item.indicator} — {item.description}</h4>
                      <p className="text-[10px] text-[#434655] mt-0.5">Source: {item.source} ({item.year})</p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 pt-2 border-t border-[#c3c6d7]/20 text-center">
                <span className="text-[10px] text-[#434655]">{feedItems.length} indicators across tracked territories</span>
              </div>
              </>
            ) : (
              <p className="text-xs text-[#434655] italic py-4 text-center">Loading economic data in background. Refresh in a moment.</p>
            )}
          </div>

          {/* Blueprint Generator */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h2 className="text-sm font-bold text-[#0b1c30] tracking-tight mb-2">Blueprint Generator</h2>
            <p className="text-xs text-[#434655] mb-4">Create an automated intelligence briefing for specific regional corridors.</p>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-[#eff4ff] flex items-center justify-center">
                <Globe className="w-4 h-4 text-[#004ac6]" />
              </div>
              <div className="w-8 h-8 rounded-lg bg-[#eff4ff] flex items-center justify-center">
                <FileText className="w-4 h-4 text-[#004ac6]" />
              </div>
              <div className="w-8 h-8 rounded-lg bg-[#eff4ff] flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-[#004ac6]" />
              </div>
            </div>
            <Button onClick={() => navigate('/platform/blueprint-config')} className="w-full bg-[#004ac6] text-white gap-2 text-xs font-bold" size="sm" data-testid="start-engine-btn">
              <Sparkles className="w-3.5 h-3.5" /> Start Engine
            </Button>
          </div>

          {/* Saved Watchlist */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h2 className="text-sm font-bold text-[#0b1c30] tracking-tight mb-3">Saved Watchlist</h2>
            <div className="space-y-1">
              {['Nordic Energy Corridor', 'MENA Stability Index', 'South China Sea Trade'].map((item, i) => (
                <button key={i} className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-[#eff4ff] transition-all text-left">
                  <span className="text-xs font-medium text-[#0b1c30]">{item}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-[#c3c6d7]" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default CountryDashboard;
